import { z } from '@hono/zod-openapi'

export const StorageClassSchema = z.enum(['hot', 'cool', 'cold', 'glacier']).openapi({
  example: 'hot',
  description: 'Storage class determining access speed and cost tier',
})

export const RetentionActionSchema = z.enum(['archive', 'delete', 'anonymize']).openapi({
  example: 'archive',
  description: 'Lifecycle action executed when the retention period expires',
})

export const RetentionPolicySchema = z.object({
  id: z.string().uuid().openapi({ example: 'rp-1234-5678' }),
  documentType: z.enum([
    'iep', '504-plan', 'evaluation', 'consent', 'notice', 'transcript',
    'progress-report', 'referral', 'eligibility', 'manifestation', 'pwn',
    'iep-amendment', 'health-plan', 'mtss-plan', 'eval-report', 's504-evaluation',
  ]).openapi({ example: 'iep' }),
  classification: z.string().min(1).max(100).openapi({ example: 'ferpa-restricted' }),
  retentionYears: z.number().int().min(0).max(999).openapi({ example: 7 }),
  action: RetentionActionSchema,
  legalHoldExempt: z.boolean().openapi({ description: 'Whether a legal hold can override this policy' }),
  regulatory: z.string().min(1).max(100).openapi({ example: 'FERPA', description: 'Regulatory authority e.g. FERPA, IDEA, WA-RCW-28A.150' }),
  // The example deliberately claims NO retention period and cites NO authority:
  // retention periods are configured from the district's records-retention
  // schedule and resolve from the registries with a verification status —
  // never typed inline (Rule 1). The previous example asserted an uncited
  // year-count as law.
  description: z.string().max(2000).openapi({ example: 'Retention policy for IEP records; period and authority are configured from the district records-retention schedule' }),
}).openapi('RetentionPolicy', { description: 'A retention policy governing document lifecycle for a given document type and classification' })

export const ArchiveRecordSchema = z.object({
  id: z.string().uuid().openapi({ example: 'arc-1234-5678' }),
  documentId: z.string().uuid().openapi({ example: 'doc-1234-5678' }),
  versionId: z.string().uuid().openapi({ example: 'ver-1234-5678' }),
  tenantId: z.string().min(1).max(64).openapi({ example: 'dist-wa-12345' }),
  sha256: z.string().length(64).openapi({ example: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855' }),
  storageLocation: z.string().min(1).max(1024).openapi({ example: 's3://cetused-archive/dist-wa-12345/arc-1234-5678.pdf' }),
  storageClass: StorageClassSchema,
  retentionUntil: z.string().datetime().openapi({ example: '2033-07-15T00:00:00Z' }),
  legalHold: z.boolean(),
  legalHoldId: z.string().uuid().nullable(),
  archivedAt: z.string().datetime(),
  archivedBy: z.string().uuid(),
  verifiedAt: z.string().datetime().nullable(),
  verifiedBy: z.string().uuid().nullable(),
  purgeScheduledFor: z.string().datetime().nullable(),
  purgedAt: z.string().datetime().nullable(),
}).openapi('ArchiveRecord', { description: 'An immutable archived document record with retention tracking and integrity verification' })

export const LegalHoldEventSchema = z.object({
  timestamp: z.string().datetime(),
  action: z.enum(['placed', 'released', 'extended', 'notification-sent', 'audit-verified']),
  actorId: z.string().uuid(),
  details: z.string().min(1).max(2000),
}).openapi('LegalHoldEvent', { description: 'A single event in the legal hold audit trail' })

export const LegalHoldSchema = z.object({
  id: z.string().uuid().openapi({ example: 'lh-1234-5678' }),
  recordIds: z.array(z.string().uuid()).min(1),
  reason: z.string().min(1).max(2000).openapi({ example: 'Pending litigation — Doe v. School District, Case No. 26-2-12345-6' }),
  placedBy: z.string().uuid(),
  placedAt: z.string().datetime(),
  releasedAt: z.string().datetime().nullable(),
  releasedBy: z.string().uuid().nullable(),
  releaseReason: z.string().max(2000).nullable(),
  notificationSent: z.boolean(),
  auditTrail: z.array(LegalHoldEventSchema),
}).openapi('LegalHold', { description: 'A legal hold placed on one or more archived records to prevent purging during litigation' })

export const PurgeLogEntrySchema = z.object({
  id: z.string().uuid(),
  recordId: z.string().uuid(),
  documentId: z.string().uuid(),
  tenantId: z.string().min(1).max(64),
  action: z.enum(['cryptographic-wipe', 'overwrite-7-pass', 'deletion-confirmed']),
  retentionVerified: z.boolean(),
  legalHoldCheckPassed: z.boolean(),
  performedBy: z.string().uuid(),
  performedAt: z.string().datetime(),
  verificationHash: z.string().length(64),
  notes: z.string().max(2000).nullable(),
}).openapi('PurgeLogEntry', { description: 'An audit log entry recording a secure purge operation' })

export const ArchiveQuerySchema = z.object({
  tenantId: z.string().min(1).max(64),
  documentId: z.string().uuid().optional(),
  documentType: z.string().optional(),
  storageClass: StorageClassSchema.optional(),
  legalHold: z.boolean().optional(),
  retentionBefore: z.string().datetime().optional(),
  retentionAfter: z.string().datetime().optional(),
  archivedBefore: z.string().datetime().optional(),
  archivedAfter: z.string().datetime().optional(),
  limit: z.number().int().min(1).max(1000).default(100),
  offset: z.number().int().min(0).default(0),
}).openapi('ArchiveQuery', { description: 'Query parameters for searching archive records' })

export type StorageClass = z.infer<typeof StorageClassSchema>
export type RetentionAction = z.infer<typeof RetentionActionSchema>
export type RetentionPolicy = z.infer<typeof RetentionPolicySchema>
export type ArchiveRecord = z.infer<typeof ArchiveRecordSchema>
export type LegalHoldEvent = z.infer<typeof LegalHoldEventSchema>
export type LegalHold = z.infer<typeof LegalHoldSchema>
export type PurgeLogEntry = z.infer<typeof PurgeLogEntrySchema>
export type ArchiveQuery = z.infer<typeof ArchiveQuerySchema>
